import React, { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import axios from "../../../api/axios";
import { fetchJournalDay, saveJournalDay, updateTodoDigestPreferences } from "../../../api/workJournal";
import { TodoTaskList } from "./TodoTaskList";
import { TodoMobileNav } from "./TodoMobileNav";
import { WorkJournalRichEditor } from "../../../components/dashboard/todos/WorkJournalRichEditor";
import { SeoHelmetWrapper } from "../../../components/Utils";
import toast from "react-hot-toast";
import {
  parseWorkspaceViewTab,
} from "./todoWorkspaceSearchParams";
import "../../../components/dashboard/projects/projectPlanSitelane.css";
import "../../../components/dashboard/todos/work-journal.css";
import "./TodoTaskList.css";

function toYmd(d) {
  const x = new Date(d);
  const p = (n) => String(n).padStart(2, "0");
  return `${x.getFullYear()}-${p(x.getMonth() + 1)}-${p(x.getDate())}`;
}

/* ─────────────────────────────────────────────
 * Journal aside — Promang side panel (dagbok)
 * ──────────────────────────────────────────── */
function JournalAside({
  journalDate,
  setJournalDate,
  shiftDay,
  journalHtml,
  journalLoading,
  journalFullscreen,
  digestMorning,
  setDigestMorning,
  digestEvening,
  setDigestEvening,
  digestTz,
  setDigestTz,
  onJournalChange,
  onOpenFullscreen,
  persistDigest,
  editorPlaceholder,
  className = "",
  t,
}) {
  const [digestOpen, setDigestOpen] = useState(false);

  return (
    <aside className={`td-journal-aside ${className}`.trim()}>
      <div className="td-journal-chrome">
        <div className="td-journal-chrome-row">
          <span className="td-journal-chrome-title">{t("todo.journalTitle")}</span>
          <span style={{ flex: 1 }} />
          <button
            type="button"
            className="td-journal-iconbtn"
            onClick={() => shiftDay(-1)}
            aria-label={t("todo.prevDay")}
            title={t("todo.prevDay")}
          >
            ‹
          </button>
          <button type="button" className="td-journal-pill" onClick={() => setJournalDate(toYmd(new Date()))}>
            {t("todo.today")}
          </button>
          <button
            type="button"
            className="td-journal-iconbtn"
            onClick={() => shiftDay(1)}
            aria-label={t("todo.nextDay")}
            title={t("todo.nextDay")}
          >
            ›
          </button>
          <button
            type="button"
            className="td-journal-iconbtn"
            onClick={onOpenFullscreen}
            disabled={journalLoading}
            aria-label={t("todo.journalFullscreen")}
            title={t("todo.journalFullscreen")}
          >
            ⤢
          </button>
        </div>
        <input
          type="date"
          className="td-journal-date"
          value={journalDate}
          onChange={(e) => setJournalDate(e.target.value)}
          aria-label={t("todo.journalDate")}
        />
        <p className="td-journal-hint">{t("todo.journalHintRich")}</p>
      </div>

      <div className="td-journal-editor-wrap">
        {journalLoading ? (
          <div className="td-journal-placeholder">{t("todo.aiLoading")}</div>
        ) : journalFullscreen ? (
          <div className="td-journal-placeholder">{t("todo.journalOpenInFullscreen")}</div>
        ) : (
          <WorkJournalRichEditor
            value={journalHtml}
            onChange={onJournalChange}
            placeholder={editorPlaceholder}
          />
        )}
      </div>

      <div className="td-digest">
        <button
          type="button"
          className={`td-digest-summary ${digestOpen ? "is-open" : ""}`}
          onClick={() => setDigestOpen((v) => !v)}
          aria-expanded={digestOpen}
        >
          <span>{t("todo.digestTitle")}</span>
          <span className="td-digest-summary-chev">›</span>
        </button>
        {digestOpen && (
          <div className="td-digest-body">
            <label className="td-digest-row">
              <input
                type="checkbox"
                checked={digestMorning}
                onChange={(e) => {
                  const v = e.target.checked;
                  setDigestMorning(v);
                  persistDigest({ todo_morning_digest_enabled: v });
                }}
              />
              <span>{t("todo.digestMorning")}</span>
            </label>
            <label className="td-digest-row">
              <input
                type="checkbox"
                checked={digestEvening}
                onChange={(e) => {
                  const v = e.target.checked;
                  setDigestEvening(v);
                  persistDigest({ todo_evening_digest_enabled: v });
                }}
              />
              <span>{t("todo.digestEvening")}</span>
            </label>
            <div className="td-digest-tz">
              <label htmlFor="td-digest-tz-input">{t("todo.digestTimezone")}</label>
              <input
                id="td-digest-tz-input"
                value={digestTz}
                onChange={(e) => setDigestTz(e.target.value)}
                onBlur={() => persistDigest({ todo_digest_timezone: digestTz })}
              />
              <span className="td-digest-tz-help">e.g. Europe/Oslo</span>
            </div>
          </div>
        )}
      </div>
    </aside>
  );
}

/* ─────────────────────────────────────────────
 * Fullscreen journal — light Promang overlay
 * ──────────────────────────────────────────── */
function FullscreenJournal({
  open,
  onClose,
  journalDate,
  setJournalDate,
  shiftDay,
  journalHtml,
  journalLoading,
  onJournalChange,
  editorPlaceholder,
  t,
}) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="td-app td-journal-fullscreen" role="dialog" aria-modal="true">
      <div className="td-journal-fullscreen-bar">
        <span className="td-journal-fullscreen-title">
          {t("todo.journalTitle")}
        </span>
        <span style={{ flex: 1 }} />
        <button
          type="button"
          className="td-journal-iconbtn"
          onClick={() => shiftDay(-1)}
          aria-label={t("todo.prevDay")}
          title={t("todo.prevDay")}
        >
          ‹
        </button>
        <button type="button" className="td-journal-pill" onClick={() => setJournalDate(toYmd(new Date()))}>
          {t("todo.today")}
        </button>
        <button
          type="button"
          className="td-journal-iconbtn"
          onClick={() => shiftDay(1)}
          aria-label={t("todo.nextDay")}
          title={t("todo.nextDay")}
        >
          ›
        </button>
        <input
          type="date"
          className="td-journal-date"
          style={{ maxWidth: 180 }}
          value={journalDate}
          onChange={(e) => setJournalDate(e.target.value)}
          aria-label={t("todo.journalDate")}
        />
        <button type="button" className="pp-btn pp-btn-primary" onClick={onClose}>
          {t("todo.journalExitFullscreen")}
        </button>
      </div>
      <div className="td-journal-fullscreen-body">
        {journalLoading ? (
          <div className="td-journal-placeholder">{t("todo.aiLoading")}</div>
        ) : (
          <WorkJournalRichEditor
            fullscreen
            value={journalHtml}
            onChange={onJournalChange}
            placeholder={editorPlaceholder}
          />
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────
 * Main workspace
 * ──────────────────────────────────────────── */
export const TodoWorkspace = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const mobileWorkspaceTab = parseWorkspaceViewTab(searchParams);

  const [journalDate, setJournalDate] = useState(() => toYmd(new Date()));
  const [journalHtml, setJournalHtml] = useState("");
  const [journalLoading, setJournalLoading] = useState(true);
  const [journalFullscreen, setJournalFullscreen] = useState(false);
  const saveTimer = useRef(null);

  const [digestMorning, setDigestMorning] = useState(false);
  const [digestEvening, setDigestEvening] = useState(false);
  const [digestTz, setDigestTz] = useState("Europe/Oslo");

  const loadSettings = useCallback(async () => {
    try {
      const res = await axios.get("/users/me/settings");
      const s = res?.data?.settings || {};
      setDigestMorning(!!s.todo_morning_digest_enabled);
      setDigestEvening(!!s.todo_evening_digest_enabled);
      setDigestTz(s.todo_digest_timezone || "Europe/Oslo");
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    let cancelled = false;
    setJournalLoading(true);
    fetchJournalDay(journalDate)
      .then((row) => {
        if (!cancelled) {
          setJournalHtml(row?.body_html || "");
        }
      })
      .catch(() => {
        if (!cancelled) setJournalHtml("");
      })
      .finally(() => {
        if (!cancelled) setJournalLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [journalDate]);

  const scheduleSave = useCallback(
    (html) => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(async () => {
        try {
          await saveJournalDay(journalDate, html);
        } catch {
          toast.error(t("todo.journalSaveError"));
        }
      }, 900);
    },
    [journalDate, t]
  );

  const onJournalChange = useCallback(
    (v) => {
      setJournalHtml(v);
      scheduleSave(v);
    },
    [scheduleSave]
  );

  const shiftDay = (delta) => {
    const d = new Date(journalDate + "T12:00:00");
    d.setDate(d.getDate() + delta);
    setJournalDate(toYmd(d));
  };

  const persistDigest = async (patch) => {
    try {
      await updateTodoDigestPreferences(patch);
      toast.success(t("todo.digestSaved"));
    } catch {
      toast.error(t("todo.digestSaveError"));
    }
  };

  const editorPlaceholder = t("todo.journalPlaceholder");

  return (
    <div className="project-plan-sitelane td-app td-workspace td-workspace-with-mobile-nav flex h-full min-h-0 flex-1 flex-col overflow-hidden bg-[#F6F8FB]">
      <SeoHelmetWrapper title={t("todo.pageTitle")} description={t("todo.pageTitle")} />

      <div className="td-workspace-body min-h-0 flex-1">
        <div
          className={`td-workspace-tasks border-r border-[#E5E9F0] ${
            mobileWorkspaceTab !== 0 ? "td-hide-on-mobile-tab" : ""
          }`}
        >
          <TodoTaskList hideHeader />
        </div>

        {/* Both panes stay mounted; mobile tab toggles a visibility class
            so the Quill editor never re-mounts mid-edit. */}
        <JournalAside
          className={mobileWorkspaceTab !== 1 ? "td-hide-on-mobile-tab" : ""}
          journalDate={journalDate}
          setJournalDate={setJournalDate}
          shiftDay={shiftDay}
          journalHtml={journalHtml}
          journalLoading={journalLoading}
          journalFullscreen={journalFullscreen}
          digestMorning={digestMorning}
          setDigestMorning={setDigestMorning}
          digestEvening={digestEvening}
          setDigestEvening={setDigestEvening}
          digestTz={digestTz}
          setDigestTz={setDigestTz}
          onJournalChange={onJournalChange}
          onOpenFullscreen={() => setJournalFullscreen(true)}
          persistDigest={persistDigest}
          editorPlaceholder={editorPlaceholder}
          t={t}
        />
      </div>

      <FullscreenJournal
        open={journalFullscreen}
        onClose={() => setJournalFullscreen(false)}
        journalDate={journalDate}
        setJournalDate={setJournalDate}
        shiftDay={shiftDay}
        journalHtml={journalHtml}
        journalLoading={journalLoading}
        onJournalChange={onJournalChange}
        editorPlaceholder={editorPlaceholder}
        t={t}
      />

      <TodoMobileNav />
    </div>
  );
};
